<template>
  <f7-popup :opened="opened" backdrop @popup:closed="handleClose" @popup:open="handlePopupOpened">
    <f7-page>
      <f7-navbar :title="$t('box.actions.addNew')">
        <f7-nav-right>
          <f7-link popup-close>{{ $t('general.close') }}</f7-link>
        </f7-nav-right>
      </f7-navbar>

      <!-- Loading state during session refresh -->
      <f7-block v-if="isRefreshingSession" class="text-align-center margin-top">
        <f7-preloader size="32" color="purple"></f7-preloader>
        <p class="text-color-gray margin-top-half">
          {{ $t('general.loading') }}
        </p>
      </f7-block>

      <!-- Form content -->
      <template v-else>
        <f7-block-title medium>{{ $t('box.form.details') }}</f7-block-title>
        <f7-list strong-ios outline-ios dividers-ios inset>
          <f7-list-input
            v-model:value="form.title"
            :label="$t('box.form.title')"
            type="text"
            :placeholder="$t('box.form.titlePlaceholder')"
            :error-message="errors.title"
            :error-message-force="!!errors.title"
            outline
            floating-label
            clear-button
            @input="clearError('title')"
          >
            <template #media>
              <f7-icon f7="cube_box_fill" color="purple"></f7-icon>
            </template>
          </f7-list-input>
        </f7-list>

        <f7-block-title medium>{{ $t('box.shipping.info') }}</f7-block-title>
        <f7-list strong-ios outline-ios dividers-ios inset>
          <!-- Name -->
          <f7-list-input
            v-model:value="form.shipName"
            :label="$t('box.shipping.name') + ' *'"
            type="text"
            :placeholder="$t('box.shipping.namePlaceholder')"
            :error-message="errors.shipName"
            :error-message-force="!!errors.shipName"
            required
            validate
            outline
            floating-label
            clear-button
            @input="clearError('shipName')"
          >
            <template #media>
              <f7-icon f7="person_fill" color="purple"></f7-icon>
            </template>
          </f7-list-input>

          <!-- Phone with proper RTL/LTR handling -->
          <f7-list-input
            v-model:value="form.shipPhone"
            :label="$t('box.shipping.phone') + ' *'"
            :placeholder="$t('box.shipping.phonePlaceholder')"
            type="tel"
            required
            validate
            outline
            floating-label
            :error-message="errors.shipPhone"
            :error-message-force="!!errors.shipPhone"
            :input-style="{ direction: 'ltr', textAlign: 'left' }"
            @input="clearError('shipPhone')"
          >
            <template #media>
              <f7-icon f7="phone_fill" color="purple"></f7-icon>
            </template>
            <template #inner-end>
              <f7-link
                v-if="form.shipPhone"
                icon-f7="xmark_circle_fill"
                icon-size="20px"
                @click="form.shipPhone = ''; clearError('shipPhone')"
              ></f7-link>
            </template>
          </f7-list-input>

          <!-- Address -->
          <f7-list-input
            v-model:value="form.shipAddress"
            :label="$t('box.shipping.address') + ' *'"
            type="textarea"
            :placeholder="$t('box.shipping.addressPlaceholder')"
            :error-message="errors.shipAddress"
            :error-message-force="!!errors.shipAddress"
            required
            validate
            outline
            floating-label
            resizable
            @input="clearError('shipAddress')"
          >
            <template #media>
              <f7-icon f7="house_fill" color="purple"></f7-icon>
            </template>
          </f7-list-input>

          <!-- Governorate Smart Select -->
          <!-- Governorate Smart Select with key to force re-render when form data changes -->
          <f7-list-item
            :key="`governorate-${form.shipGovernorate}`"
            :title="$t('box.shipping.governorate') + ' *'"
            smart-select
            :smart-select-params="{ openIn: 'popover', searchbar: true, searchbarPlaceholder: $t('general.search') }"
          >
            <template #media>
              <f7-icon f7="map_fill" color="purple"></f7-icon>
            </template>
            <select v-model="form.shipGovernorate" name="governorate" @change="clearError('shipGovernorate')">
              <option value="">
                {{ $t('general.selectGovernorate') }}
              </option>
              <option value="cairo">
                {{ $t('governorates.cairo') }}
              </option>
              <option value="alexandria">
                {{ $t('governorates.alexandria') }}
              </option>
              <option value="giza">
                {{ $t('governorates.giza') }}
              </option>
              <option value="qalyubia">
                {{ $t('governorates.qalyubia') }}
              </option>
              <option value="sharqia">
                {{ $t('governorates.sharqia') }}
              </option>
              <option value="gharbia">
                {{ $t('governorates.gharbia') }}
              </option>
              <option value="menoufia">
                {{ $t('governorates.menoufia') }}
              </option>
              <option value="beheira">
                {{ $t('governorates.beheira') }}
              </option>
              <option value="ismailia">
                {{ $t('governorates.ismailia') }}
              </option>
              <option value="dakahlia">
                {{ $t('governorates.dakahlia') }}
              </option>
              <option value="damietta">
                {{ $t('governorates.damietta') }}
              </option>
              <option value="kafr_el_sheikh">
                {{ $t('governorates.kafr_el_sheikh') }}
              </option>
              <option value="fayoum">
                {{ $t('governorates.fayoum') }}
              </option>
              <option value="beni_suef">
                {{ $t('governorates.beni_suef') }}
              </option>
              <option value="minya">
                {{ $t('governorates.minya') }}
              </option>
              <option value="assiut">
                {{ $t('governorates.assiut') }}
              </option>
              <option value="sohag">
                {{ $t('governorates.sohag') }}
              </option>
              <option value="qena">
                {{ $t('governorates.qena') }}
              </option>
              <option value="aswan">
                {{ $t('governorates.aswan') }}
              </option>
              <option value="luxor">
                {{ $t('governorates.luxor') }}
              </option>
              <option value="red_sea">
                {{ $t('governorates.red_sea') }}
              </option>
              <option value="new_valley">
                {{ $t('governorates.new_valley') }}
              </option>
              <option value="matrouh">
                {{ $t('governorates.matrouh') }}
              </option>
              <option value="north_sinai">
                {{ $t('governorates.north_sinai') }}
              </option>
              <option value="south_sinai">
                {{ $t('governorates.south_sinai') }}
              </option>
              <option value="port_said">
                {{ $t('governorates.port_said') }}
              </option>
              <option value="suez">
                {{ $t('governorates.suez') }}
              </option>
              <option value="other">
                {{ $t('governorates.other') }}
              </option>
            </select>
          </f7-list-item>

          <!-- Show validation error for governorate if any -->
          <f7-list-item v-if="errors.shipGovernorate">
            <span class="text-color-red" style="font-size: 12px; padding: 0 16px;">
              {{ errors.shipGovernorate }}
            </span>
          </f7-list-item>
        </f7-list>

        <f7-block>
          <f7-button fill
            large
            color="purple"
            :disabled="isCreating"
            @click="handleSubmit"
          >
            <f7-preloader v-if="isCreating" size="20" class="margin-right-half"></f7-preloader>
            <f7-icon v-else f7="plus_circle_fill" class="margin-right-half"></f7-icon>
            {{ isCreating ? $t('general.loading') : $t('box.actions.create') }}
          </f7-button>
        </f7-block>
      </template>
    </f7-page>
  </f7-popup>
</template>

<script setup>
import { useAsyncState } from '@vueuse/core';
import { f7 } from 'framework7-vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuth } from '@/composables/useAuth';

defineProps({
  opened: {
    type: Boolean,
    default: false,
  },
  nextBoxNumber: {
    type: Number,
    required: true,
  },
  isCreating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:opened', 'create']);

const { t } = useI18n();

// Auth composable for accessing user data
const { user, organization, membership, refreshSession } = useAuth();

const form = ref({
  title: '',
  shipName: '',
  shipPhone: '',
  shipAddress: '',
  shipGovernorate: '',
});

const errors = ref({
  title: '',
  shipName: '',
  shipPhone: '',
  shipAddress: '',
  shipGovernorate: '',
});

// Session refresh state using useAsyncState
const { isLoading: isRefreshingSession, execute: executeRefreshSession } = useAsyncState(
  refreshSession,
  null,
  { immediate: false },
);

// Computed default title based on membership's maxBoxesNumber
const defaultTitle = computed(() => {
  const boxNumber = (membership.value?.maxBoxesNumber || 0) + 1;
  return `كرتونة #${boxNumber}`;
});

// Fill form with user data from authStore
const fillFormWithUserData = () => {
  form.value.title = defaultTitle.value;
  form.value.shipName = user.value?.name || '';
  form.value.shipPhone = organization.value?.phone || '';
  form.value.shipAddress = organization.value?.address || '';
  form.value.shipGovernorate = organization.value?.governorate || '';

  // Clear all errors
  errors.value = {
    title: '',
    shipName: '',
    shipPhone: '',
    shipAddress: '',
    shipGovernorate: '',
  };

  console.log('[AddBoxPopup] Form filled with:', {
    title: form.value.title,
    shipName: form.value.shipName,
    shipGovernorate: form.value.shipGovernorate,
  });
};

const resetForm = () => {
  form.value = {
    title: defaultTitle.value,
    shipName: '',
    shipPhone: '',
    shipAddress: '',
    shipGovernorate: '',
  };

  errors.value = {
    title: '',
    shipName: '',
    shipPhone: '',
    shipAddress: '',
    shipGovernorate: '',
  };
};

const clearError = (field) => {
  errors.value[field] = '';
};

const validateForm = () => {
  let isValid = true;

  // Reset errors
  Object.keys(errors.value).forEach(key => {
    errors.value[key] = '';
  });

  // Validate title
  if (!form.value.title || !form.value.title.trim()) {
    errors.value.title = t('general.requiredField');
    isValid = false;
  }

  // Validate shipName
  if (!form.value.shipName || !form.value.shipName.trim()) {
    errors.value.shipName = t('general.requiredField');
    isValid = false;
  }

  // Validate shipPhone
  if (!form.value.shipPhone || !form.value.shipPhone.trim()) {
    errors.value.shipPhone = t('general.requiredField');
    isValid = false;
  } else if (!/^\+?[0-9]{10,15}$/.test(form.value.shipPhone.replace(/\s/g, ''))) {
    errors.value.shipPhone = t('general.invalidPhone');
    isValid = false;
  }

  // Validate shipAddress
  if (!form.value.shipAddress || !form.value.shipAddress.trim()) {
    errors.value.shipAddress = t('general.requiredField');
    isValid = false;
  }

  // Validate shipGovernorate
  if (!form.value.shipGovernorate || !form.value.shipGovernorate.trim()) {
    errors.value.shipGovernorate = t('general.requiredField');
    isValid = false;
  }

  return isValid;
};

const handleSubmit = () => {
  if (!validateForm()) {
    f7.toast.create({
      text: t('box.shipping.fillRequired'),
      position: 'center',
      closeTimeout: 2000,
    }).open();
    return;
  }

  const newBoxData = {
    title: form.value.title.trim(),
    shipName: form.value.shipName.trim(),
    shipPhone: form.value.shipPhone.trim(),
    shipAddress: form.value.shipAddress.trim(),
    shipGovernorate: form.value.shipGovernorate.trim(),
  };

  emit('create', newBoxData);
};

const handleClose = () => {
  emit('update:opened', false);
  // Reset form after animation completes
  setTimeout(resetForm, 300);
};

// Handle popup opened event - refresh session and fill form
const handlePopupOpened = async () => {
  console.log('[AddBoxPopup] Popup opened, refreshing session...');

  try {
    // Refresh session to get latest user data
    await executeRefreshSession();

    console.log('[AddBoxPopup] Session refreshed, filling form...');

    // Fill form with refreshed data
    fillFormWithUserData();

    console.log('[AddBoxPopup] Form filled successfully');
  } catch (error) {
    console.error('[AddBoxPopup] Failed to refresh session:', error);

    // Still try to fill form with existing data
    fillFormWithUserData();

    // Optionally show a toast notification
    f7.toast.create({
      text: t('general.updateDataFailed') || 'Failed to refresh data',
      position: 'center',
      closeTimeout: 2000,
    }).open();
  }
};
</script>

<style scoped>
/* Ensure phone input displays properly in LTR regardless of app direction */
:deep(.item-input-wrap input[type="tel"]) {
    direction: ltr !important;
    text-align: left !important;
}

/* Adjust clear button position for phone input */
:deep(.item-input-wrap) {
    position: relative;
}

:deep(.item-inner-end) {
    margin-inline-start: 8px;
}
</style>
